import { useState, useMemo, useCallback } from 'react';
import Select from 'react-select';
import FeatherIcon from 'feather-icons-react';
import {
    Form,
    Row,
    Col,
    FormGroup,
    Label,
    Button,
    Collapse,
    Input,
} from 'reactstrap';

const PropertySelector = ({
    propertyList = [],
    selectedFilters,
    onPropertySelectionChange,
    onAddClick,
    isLoading = false,
    mode,
    onModeChange,
    onOpenInvitationModal,
    statusCounts = {},
}) => {
    const [isFiltersOpen, setIsFiltersOpen] = useState(false);

    const currentProperty = useMemo(
        () => propertyList.find(p => p._id === selectedFilters.propertyId) ?? null,
        [propertyList, selectedFilters.propertyId]
    );

    const buildings = useMemo(() => {
        if (!currentProperty?.building) return [];
        return currentProperty.building
            .filter(b => b?.buildingName)
            .map(b => b.buildingName);
    }, [currentProperty]);

    const hasBuildings = buildings.length > 0;
    const hasMultipleBuildings = buildings.length > 1;

    const propertyOptions = useMemo(
        () => propertyList.map(p => ({
            value: p._id,
            label: `${p.title || 'Unnamed'}${p.location ? ` (${p.location})` : ''}`,
        })),
        [propertyList]
    );

    const buildingOptions = useMemo(
        () => buildings.map(b => ({ value: b, label: b })),
        [buildings]
    );

    const selectedPropertyOption = useMemo(
        () => propertyOptions.find(o => o.value === selectedFilters.propertyId) || null,
        [propertyOptions, selectedFilters.propertyId]
    );

    const selectedBuildingOption = useMemo(
        () => buildingOptions.find(o => o.value === selectedFilters.buildingName) || null,
        [buildingOptions, selectedFilters.buildingName]
    );

    const isAddButtonDisabled = !selectedFilters.propertyId || (hasBuildings && !selectedFilters.buildingName);

    const handlePropertyChange = useCallback((selected) => {
        const propertyId = selected?.value || '';
        const prop = propertyList.find(p => p._id === propertyId);
        const propBuildings = prop?.building?.filter(b => b?.buildingName)?.map(b => b.buildingName) ?? [];
        const autoSelectBuilding = propBuildings.length === 1 ? propBuildings[0] : '';

        onPropertySelectionChange({
            ...selectedFilters,
            propertyId,
            buildingName: autoSelectBuilding,
        });
    }, [propertyList, selectedFilters, onPropertySelectionChange]);

    const handleBuildingChange = useCallback((selected) => {
        onPropertySelectionChange({
            ...selectedFilters,
            buildingName: selected?.value || '',
        });
    }, [selectedFilters, onPropertySelectionChange]);

    const handleSearchChange = useCallback((e) => {
        onPropertySelectionChange({
            ...selectedFilters,
            search: e.target.value,
        });
    }, [selectedFilters, onPropertySelectionChange]);

    const handleStartDateChange = useCallback((e) => {
        onPropertySelectionChange({
            ...selectedFilters,
            startDate: e.target.value,
        });
    }, [selectedFilters, onPropertySelectionChange]);

    const handleEndDateChange = useCallback((e) => {
        onPropertySelectionChange({
            ...selectedFilters,
            endDate: e.target.value,
        });
    }, [selectedFilters, onPropertySelectionChange]);

    const toggleFilters = () => setIsFiltersOpen(p => !p);

    const statusButtons = [
        { value: 'all', label: 'All', icon: 'list', color: 'secondary' },
        { value: 'Open', label: 'Open', icon: 'alert-triangle', color: 'warning' },
        { value: 'InProgress', label: 'In Progress', icon: 'check-circle', color: 'success' },
        { value: 'Pending', label: 'Pending', icon: 'activity', color: 'danger' },
        { value: 'Resolved', label: 'Resolved', icon: 'tool', color: 'info' },
    ];

    return (
        <>
            <Form className="mb-4">
                <Row className="mb-3 g-3">
                    <Col>
                        <div className="d-flex flex-wrap gap-4">
                            {statusButtons.map(s => (
                                <FormGroup check inline key={s.value}>
                                    <Input
                                        type="radio"
                                        name="status"
                                        checked={selectedFilters.status === s.value}
                                        onChange={() => onPropertySelectionChange({ ...selectedFilters, status: s.value })}
                                    />
                                    <Label check className={`text-${s.color}`}>
                                        <FeatherIcon icon={s.icon} size={16} className="me-1" />
                                        {s.label}
                                        {statusCounts[s.value] !== undefined && (
                                            <span className="ms-1">({statusCounts[s.value]})</span>
                                        )}
                                    </Label>
                                </FormGroup>
                            ))}
                        </div>
                    </Col>
                </Row>

                <Row className="g-3 align-items-end">
                    <Col md={2} className="mt-0 mb-2">
                        <FormGroup>
                            <Label className="fw-semibold">View Mode</Label>
                            <div className="d-flex gap-3 pt-2">
                                <FormGroup check inline>
                                    <Input
                                        type="radio"
                                        name="mode"
                                        checked={mode === 'reports'}
                                        onChange={() => onModeChange('reports')}
                                    />
                                    <Label check>Reports</Label>
                                </FormGroup>
                                <FormGroup check inline>
                                    <Input
                                        type="radio"
                                        name="mode"
                                        checked={mode === 'invitations'}
                                        onChange={() => onModeChange('invitations')}
                                    />
                                    <Label check>Invitations</Label>
                                </FormGroup>
                            </div>
                        </FormGroup>
                    </Col>

                    <Col md={3}>
                        <FormGroup>
                            <Label className="fw-semibold">Property *</Label>
                            <Select
                                value={selectedPropertyOption}
                                onChange={handlePropertyChange}
                                options={propertyOptions}
                                isClearable
                                isDisabled={isLoading}
                                placeholder="Select property"
                                menuPortalTarget={document.body}
                            />
                        </FormGroup>
                    </Col>

                    <Col md={3}>
                        {hasBuildings && (
                            <FormGroup>
                                <Label>Building {hasMultipleBuildings ? '*' : '(auto)'}</Label>
                                <Select
                                    value={selectedBuildingOption}
                                    onChange={handleBuildingChange}
                                    options={buildingOptions}
                                    isClearable={hasMultipleBuildings}
                                    isDisabled={!hasMultipleBuildings || isLoading}
                                    menuPortalTarget={document.body}
                                />
                            </FormGroup>
                        )}
                    </Col>

                    <Col md={4} className="text-md-end">
                        <Button color="light" outline className="me-2" onClick={toggleFilters} disabled={isLoading}>
                            <FeatherIcon icon="filter" size={16} className="me-1" /> Filters
                        </Button>
                        <Button
                            color="primary"
                            onClick={onAddClick}
                            disabled={isAddButtonDisabled || isLoading}
                            className="me-2"
                        >
                            <FeatherIcon icon="plus-circle" size={16} className="me-1" /> New Incident Report
                        </Button>
                        <Button
                            color="info"
                            outline
                            onClick={() => onOpenInvitationModal(null)}
                            disabled={isLoading || !selectedFilters.propertyId || (hasBuildings && !selectedFilters.buildingName)}
                        >
                            <FeatherIcon icon="send" size={16} className="me-1" /> Send Invitation
                        </Button>
                    </Col>
                </Row>

                <Collapse isOpen={isFiltersOpen}>
                    <Row className="g-3 mt-4">
                        <Col md={6}>
                            <FormGroup>
                                <Label>Search</Label>
                                <Input
                                    type="text"
                                    value={selectedFilters.search || ''}
                                    onChange={handleSearchChange}
                                    placeholder="Address, person name, statement..."
                                />
                            </FormGroup>
                        </Col>
                        <Col md={3}>
                            <FormGroup>
                                <Label>Notification to Manager Date (From)</Label>
                                <Input
                                    type="date"
                                    value={selectedFilters.startDate || ''}
                                    onChange={handleStartDateChange}
                                />
                            </FormGroup>
                        </Col>
                        <Col md={3}>
                            <FormGroup>
                                <Label>Notification to Manager Date (To)</Label>
                                <Input
                                    type="date"
                                    value={selectedFilters.endDate || ''}
                                    onChange={handleEndDateChange}
                                />
                            </FormGroup>
                        </Col>
                    </Row>
                </Collapse>
            </Form>
        </>
    );
};

export { PropertySelector };